import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Search, Bell, Sun, Moon, LogOut, ChevronDown, X, Grid, BookOpen } from "lucide-react";
import { NAV } from "./nav";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "./ui";
import api from "../lib/api";

function useTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem("hrms_theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("hrms_theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, () => setDark((d) => !d)];
}

// Curated icons shown in the thin rail (rest live in the App Launcher)
const RAIL_PATHS = ["/", "/me", "/employees", "/leave", "/attendance", "/payroll", "/performance", "/recruitment", "/calendar", "/reports", "/approvals"];
const RAIL_BOTTOM = ["/settings", "/admin"];

const flatNav = (role) =>
  NAV.flatMap((g) => g.items).filter((i) => !i.roles || i.roles.includes(role));

function RailIcon({ item, onLauncherClose }) {
  return (
    <NavLink to={item.to} end={item.exact} onClick={onLauncherClose}
      data-testid={`rail-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={({ isActive }) =>
        `group relative flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${
          isActive ? "bg-slate-800 text-sky-400" : "text-slate-400 hover:bg-slate-800 hover:text-white"
        }`}>
      <item.icon size={19} />
      <span className="pointer-events-none absolute left-12 z-50 whitespace-nowrap rounded-md bg-slate-800 px-2 py-1 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
        {item.label}
      </span>
    </NavLink>
  );
}

function IconRail({ role, onOpenLauncher }) {
  const all = flatNav(role);
  const top = RAIL_PATHS.map((p) => all.find((i) => i.to === p)).filter(Boolean);
  const bottom = RAIL_BOTTOM.map((p) => all.find((i) => i.to === p)).filter(Boolean);
  return (
    <aside data-testid="icon-rail" className="fixed inset-y-0 left-0 z-50 flex w-16 flex-col items-center border-r border-slate-800 bg-slate-900 py-3">
      <div className="mb-1 flex h-10 w-10 items-center justify-center rounded-lg bg-accent font-head text-sm font-extrabold text-white">SM</div>
      <button data-testid="app-launcher-toggle" onClick={onOpenLauncher} aria-label="All apps"
        className="group relative mb-3 flex h-10 w-10 items-center justify-center rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white">
        <Grid size={20} />
        <span className="pointer-events-none absolute left-12 z-50 whitespace-nowrap rounded-md bg-slate-800 px-2 py-1 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">All Apps</span>
      </button>
      <div className="flex flex-1 flex-col items-center gap-1.5 overflow-y-auto no-scrollbar">
        {top.map((i) => <RailIcon key={i.to} item={i} />)}
      </div>
      <div className="mt-2 flex flex-col items-center gap-1.5 border-t border-slate-800 pt-3">
        {bottom.map((i) => <RailIcon key={i.to} item={i} />)}
      </div>
    </aside>
  );
}

function AppLauncher({ role, open, onClose }) {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  const go = (to) => { nav(to); onClose(); setQ(""); };
  const query = q.trim().toLowerCase();
  return (
    <div data-testid="app-launcher-overlay" className="fixed inset-0 z-[60] flex flex-col bg-slate-900 animate-fade-up">
      <div className="flex h-16 items-center justify-between border-b border-slate-800 px-6">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-accent font-head text-sm font-extrabold text-white">SM</div>
          <span className="font-head text-lg font-extrabold tracking-tight text-white">SMIFS <span className="text-slate-500">HRMS</span></span>
        </div>
        <button data-testid="app-launcher-close" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white">
          <X size={20} />
        </button>
      </div>
      <div className="mx-auto w-full max-w-7xl flex-1 overflow-y-auto px-6 py-8 md:px-10">
        <div className="relative mx-auto mb-10 max-w-2xl">
          <Search size={20} className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input autoFocus data-testid="app-launcher-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search apps…"
            className="w-full rounded-2xl border border-slate-700 bg-slate-800/60 py-4 pl-14 pr-6 text-lg text-white placeholder-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/40" />
        </div>
        {NAV.map((sec) => {
          const items = sec.items.filter((i) => (!i.roles || i.roles.includes(role)) && (!query || i.label.toLowerCase().includes(query)));
          if (!items.length) return null;
          return (
            <div key={sec.group} className="mb-9">
              <div className="mb-5 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{sec.group}</div>
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7">
                {items.map((i) => (
                  <button key={i.to} onClick={() => go(i.to)}
                    data-testid={`launcher-${i.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="group flex flex-col items-center gap-3 rounded-xl border border-transparent p-4 text-slate-300 transition-all hover:-translate-y-0.5 hover:border-slate-700 hover:bg-slate-800/70 hover:text-white">
                    <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-700 to-slate-800 text-sky-400 shadow-inner transition-colors group-hover:from-accent group-hover:to-blue-600 group-hover:text-white">
                      <i.icon size={24} />
                    </span>
                    <span className="text-center text-xs font-medium leading-tight">{i.label}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef();

  useEffect(() => {
    if (q.length < 2) { setRes([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setRes(data); setOpen(true); } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div className="relative w-full max-w-xl" ref={ref}>
      <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
      <input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => res.length && setOpen(true)}
        placeholder="Search for people and settings"
        className="h-10 w-full rounded-full border border-slate-200 bg-slate-50 pl-11 pr-24 text-sm text-slate-700 placeholder:text-slate-400 focus:border-accent focus:bg-white focus:outline-none focus:ring-2 focus:ring-accent/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200" />
      <button data-testid="guide-pill" type="button"
        className="absolute right-1.5 top-1/2 flex -translate-y-1/2 items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 shadow-sm hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
        <BookOpen size={13} /> Guide
      </button>
      {open && res.length > 0 && (
        <div className="absolute left-0 right-0 top-12 z-50 card overflow-hidden py-1" data-testid="search-results">
          {res.map((r) => (
            <button key={r.id} onMouseDown={() => { nav(r.url); setOpen(false); setQ(""); }}
              className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-800">
              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-slate-500 dark:bg-slate-700">{r.type}</span>
              <span className="flex-1">
                <span className="block text-sm font-medium text-slate-800 dark:text-slate-100">{r.label}</span>
                <span className="block text-xs text-slate-400">{r.subtitle}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const unread = items.filter((n) => !n.read).length;

  const load = async () => { try { const { data } = await api.get("/notifications"); setItems(data); } catch {} };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);

  const markRead = async (id) => { await api.post(`/notifications/${id}/read`); load(); };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell" onClick={() => setOpen((o) => !o)}
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800">
        <Bell size={19} />
        {unread > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white" data-testid="notification-count">{unread > 99 ? "99+" : unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-12 z-50 w-80 card overflow-hidden" data-testid="notification-panel">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5 dark:border-slate-800">
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">Notifications</span>
            <button onClick={() => setOpen(false)}><X size={15} className="text-slate-400" /></button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 && <p className="px-4 py-8 text-center text-sm text-slate-400">No notifications</p>}
            {items.map((n) => (
              <button key={n.id} onClick={() => markRead(n.id)}
                className={`block w-full border-b border-slate-100 px-4 py-3 text-left last:border-0 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/60 ${!n.read ? "bg-accent/5" : ""}`}>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{n.title}</p>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{n.body}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [dark, toggle] = useTheme();
  const [menu, setMenu] = useState(false);
  const [launcher, setLauncher] = useState(false);
  const role = user?.role || "EMPLOYEE";
  const name = user?.employee?.fullName || user?.email || "";
  const location = useLocation();
  useEffect(() => { setLauncher(false); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <IconRail role={role} onOpenLauncher={() => setLauncher(true)} />
      <AppLauncher role={role} open={launcher} onClose={() => setLauncher(false)} />

      <header className="fixed left-16 right-0 top-0 z-30 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/85 px-4 backdrop-blur-md dark:border-slate-800 dark:bg-slate-900/85 sm:px-6" data-testid="header">
        <div className="hidden items-center gap-2 sm:flex">
          <span className="font-head text-lg font-extrabold tracking-tight text-slate-900 dark:text-white">SMIFS</span>
        </div>
        <div className="flex flex-1 justify-center">
          <GlobalSearch />
        </div>
        <div className="flex items-center gap-1.5">
          <button data-testid="theme-toggle" onClick={toggle} className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800">
            {dark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <NotificationBell />
          <div className="relative">
            <button data-testid="profile-menu" onClick={() => setMenu((m) => !m)} className="flex items-center gap-2 rounded-full px-1 py-1 hover:bg-slate-100 dark:hover:bg-slate-800 sm:pl-2">
              <Avatar name={name} size={32} />
              <div className="hidden text-left sm:block">
                <div className="text-sm font-medium leading-tight text-slate-800 dark:text-slate-100">{name}</div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{role}</div>
              </div>
              <ChevronDown size={15} className="hidden text-slate-400 sm:block" />
            </button>
            {menu && (
              <div className="absolute right-0 top-12 z-50 w-44 card overflow-hidden py-1">
                <button data-testid="logout-button" onClick={logout} className="flex w-full items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
                  <LogOut size={15} /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <main className="ml-16 px-4 pb-16 pt-24 sm:px-6 lg:px-8" data-testid="main-content">{children}</main>
    </div>
  );
}
